import { Game } from "./Game";
import { config, resetConfig } from "./config/GameConfig";
import { hydrateConfig } from "./config/persist";
import { applyLevel, LEVELS } from "./levels";
import type { Level } from "./levels/types";
import {
  continueLevelId,
  devLevelOverride,
  getBootLevel,
  hasCampaignProgress,
  markLevelCleared,
  nextLevelId,
} from "./progress/campaign";
import { GamePanel } from "./ui/GamePanel";

function requireHost(): HTMLElement {
  const el = document.getElementById("game");
  if (!el) throw new Error("Missing #game root");
  return el;
}

const root = requireHost();

let game: Game | null = null;
let booting = false;

const panel = new GamePanel(root, (id) => {
  void pickLevel(id);
});

async function boot(level: Level): Promise<void> {
  if (booting) return;
  booting = true;
  try {
    panel.hide();
    if (game) {
      game.dispose();
      game = null;
    }
    resetConfig(config);
    applyLevel(config, level);
    await hydrateConfig(config, level.id);
    const returning = hasCampaignProgress() && !devLevelOverride();
    const next = new Game(root, level, {
      onWinContinue: () => {
        void afterWin(level);
      },
      onOpenPanel: returning ? () => panel.show() : undefined,
    });
    try {
      await next.start();
    } catch (err) {
      next.dispose();
      throw err;
    }
    game = next;
    panel.bringToFront(root);
  } finally {
    booting = false;
  }
}

async function afterWin(level: Level): Promise<void> {
  markLevelCleared(level.id);
  const nextId = nextLevelId(level.id);
  if (nextId && LEVELS[nextId]) {
    await boot(LEVELS[nextId]);
    return;
  }
  await boot(level);
}

async function pickLevel(id: string): Promise<void> {
  const level = LEVELS[id];
  if (!level) return;
  panel.hide();
  if (game?.level.id === id) {
    game.showTitle();
    return;
  }
  await boot(level);
}

const first = getBootLevel();
const start = hasCampaignProgress() && !devLevelOverride() ? (LEVELS[continueLevelId()] ?? first) : first;

void boot(start).catch((err: unknown) => {
  console.error("Onirya failed to start", err);
});
